import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useSidePanel } from '../../context/SidePanelContext';
import { getRRGData, getStockRRGData, getStockNiftyRRGData } from '../../api/client';
import InfoBtn from '../ui/InfoBtn';
import { RRG_TICKER_MAP, RRG_TICKER_TO_KITE } from '../../data/indices';

// ── Module-level caches ────────────────────────────────────────────────────────
// Each entry: { stocks/sectors, computedAt, _ts } where _ts is Date.now() at fetch time.
// TTL prevents stale data after sector assignments or universe refreshes.
const _CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes
const _RRG_CACHE = {};
const _STOCK_RRG_CACHE = {};       // key: "sector1|sector2__{algo}__{tf}__{len}" → { stocks, computedAt, _ts }
const _NIFTY_STOCK_RRG_CACHE = {}; // key: "{tf}__{len}" → { stocks, computedAt, _ts }

function _cacheValid(entry) {
  return entry && (Date.now() - (entry._ts || 0)) < _CACHE_TTL_MS;
}

// ── Session-persistent panel open/close state ─────────────────────────────────
// Survives tab switches (React unmount/remount) within the same browser session.
// chart1Open defaults true so Chart 1 is always expanded on first load.
const _PANEL_STATE = {
  chart1Open: true, stockOpen: false, niftyOpen: false,
  // Chart parameter values — persist within the same browser session (module stays loaded)
  // Defaults match the useState(...) initialisers below.
  wmaLength: 20, timeframe: 'weekly', tailLen: 10,
  stockTimeframe: 'weekly', stockWmaLength: 20, stockTailLen: 10,
  niftyTimeframe: 'weekly', niftyLength: 20, niftyTailLen: 10,
};

// ── RRG sector ID → SECTOR_MAP / SECTOR_TO_INDEX key ────────────────────────
// Used when switching to Stock RRG view to map the enabled sector IDs to the
// backend's SECTOR_TO_INDEX keys.
const RRG_ID_TO_SECTOR_KEY = {
  niftyit:              'IT',
  niftybank:            'Banking',
  niftyfinsvc:          'Financial Services',
  niftypharma:          'Pharmaceuticals',
  niftyhealth:          'Healthcare',
  niftyauto:            'Automobile',
  niftymetal:           'Metals',
  niftyfmcg:            'FMCG',
  niftyconsum:          'Consumer Durables',
  niftyenergy:          'Energy',
  niftyinfra:           'Infrastructure',
  niftyrealty:          'Real Estate',
  niftymedia:           'Media & Entertainment',
  synth_aviation:       'Aviation',
  synth_cement:         'Cement',
  synth_chemicals:      'Chemicals',
  synth_conglomerates:  'Conglomerates',
  synth_ports_logistics:'Ports & Logistics',
  synth_retail:         'Retail',
  synth_technology:     'Technology',
  synth_telecom:        'Telecom',
};

// All valid sector keys (ordered for the filter chips in stock view)
const ALL_SECTOR_KEYS = [
  'IT', 'Banking', 'Financial Services', 'Pharmaceuticals', 'Healthcare',
  'Automobile', 'Metals', 'FMCG', 'Consumer Durables', 'Energy',
  'Infrastructure', 'Real Estate', 'Media & Entertainment',
  'Aviation', 'Cement', 'Chemicals', 'Conglomerates', 'Ports & Logistics',
  'Retail', 'Technology', 'Telecom',
];

const TICKER_MAP = RRG_TICKER_MAP;

// ── Helpers ───────────────────────────────────────────────────────────────────
function getQuadrant(x, y) {
  // Canonical boundary: strictly > 100 = leading half (matches backend rrg_quadrant()).
  // Exactly 100 on either axis → left/bottom (weakening or lagging).
  if (x >  100 && y >  100) return 'leading';
  if (x >  100 && y <= 100) return 'weakening';
  if (x <= 100 && y <= 100) return 'lagging';
  return 'improving';
}

const QUAD_META = {
  leading:   { label: 'Leading',   abbr: 'LD', bg: 'rgba(26,106,26,.10)',  border: 'rgba(26,106,26,.30)',  dot: 'rgba(26,106,26,.9)',  text: '#1a6a1a' },
  weakening: { label: 'Weakening', abbr: 'WK', bg: 'rgba(138,90,0,.10)',   border: 'rgba(138,90,0,.30)',   dot: 'rgba(138,90,0,.9)',   text: '#8a5a00' },
  lagging:   { label: 'Lagging',   abbr: 'LG', bg: 'rgba(136,30,16,.10)',  border: 'rgba(136,30,16,.30)',  dot: 'rgba(136,30,16,.9)',  text: '#881e10' },
  improving: { label: 'Improving', abbr: 'IM', bg: 'rgba(26,74,138,.10)',  border: 'rgba(26,74,138,.30)',  dot: 'rgba(26,74,138,.9)',  text: '#1a4a8a' },
};

const QUAD_FILL = {
  leading:   'rgba(26,106,26,.065)',
  weakening: 'rgba(138,90,0,.065)',
  lagging:   'rgba(136,30,16,.065)',
  improving: 'rgba(26,74,138,.065)',
};

function secColorFromTrail(trail) {
  const cur = trail[trail.length - 1];
  return { leading: '#1a6a1a', weakening: '#8a5a00', lagging: '#881e10', improving: '#1a4a8a' }[getQuadrant(cur.x, cur.y)];
}

function hexToRgba(hex, alpha) {
  if (hex.startsWith('rgba') || hex.startsWith('rgb'))
    return hex.replace(/[\d.]+\)$/, `${alpha})`);
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function parseSectors(res) {
  return (res?.sectors || []).map(s => ({
    id:     s.id,
    name:   s.label,
    ticker: s.ticker || TICKER_MAP[s.id] || s.id.toUpperCase(),
    ltp:    s.ltp    ?? 0,
    chg:    s.chg    ?? 0,
    chgPct: s.chg_pct ?? 0,
    trail:  s.trail  ?? [],
  }));
}

// ── Main Chart Canvas ─────────────────────────────────────────────────────────
const PAD = { l: 44, r: 24, t: 20, b: 36 };

function RRGCanvas({ sectors, tailLen, enabled }) {
  const canvasRef      = useRef(null);
  const labelBounds    = useRef([]);
  const [tooltip, setTooltip] = useState(null);
  const [cursor,  setCursor]  = useState('crosshair');

  // Zoom / pan refs
  const zoomRef = useRef(1);
  const panXRef = useRef(0);
  const panYRef = useRef(0);
  const cx0Ref  = useRef(0);
  const cy0Ref  = useRef(0);

  // Drag
  const isDragging = useRef(false);
  const dragStart  = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

  // Animation: { [sectorId]: startTimeMs }
  const animatingRef   = useRef({});
  const prevEnabledRef = useRef(new Set());
  const rafRef         = useRef(null);

  // When enabled set changes:
  //   1. Immediately reset zoom/pan and draw at the new auto-fit scale.
  //   2. On the next animation frame, register newly-enabled sectors for trail
  //      animation — so the canvas has already settled before trails start drawing.
  useEffect(() => {
    // Step 1 — zoom/pan reset + draw (synchronous, before browser paint)
    zoomRef.current = 1;
    panXRef.current = 0;
    panYRef.current = 0;
    drawRef.current();

    // Step 2 — start animation after one frame (canvas already at correct scale)
    const raf = requestAnimationFrame(() => {
      const now = performance.now();
      enabled.forEach(id => {
        if (!prevEnabledRef.current.has(id)) {
          animatingRef.current[id] = now;
        }
      });
      prevEnabledRef.current.forEach(id => {
        if (!enabled.has(id)) delete animatingRef.current[id];
      });
      prevEnabledRef.current = new Set(enabled);
      drawRef.current();
    });
    return () => cancelAnimationFrame(raf);
  }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Core draw
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr  = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    canvas.width  = rect.width  * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    const W = rect.width, H = rect.height;
    const cW = W - PAD.l - PAD.r;
    const cH = H - PAD.t - PAD.b;

    const vis = sectors.filter(s => enabled.has(s.id));
    // Separate X and Y ranges so each axis fills the canvas fully
    let maxDistX = 1.5, maxDistY = 1.5;
    vis.forEach(sec => {
      sec.trail.slice(-tailLen).forEach(pt => {
        maxDistX = Math.max(maxDistX, Math.abs(pt.x - 100));
        maxDistY = Math.max(maxDistY, Math.abs(pt.y - 100));
      });
    });
    const RANGE_X = maxDistX + 0.8;
    const RANGE_Y = maxDistY + 0.8;

    const cx0 = PAD.l + cW / 2;
    const cy0 = PAD.t + cH / 2;
    cx0Ref.current = cx0;
    cy0Ref.current = cy0;

    const zoom    = zoomRef.current;
    const panX    = panXRef.current;
    const panY    = panYRef.current;
    const unitPxX = (cW / 2) / RANGE_X;
    const unitPxY = (cH / 2) / RANGE_Y;

    const toX = v => cx0 + (v - 100) * unitPxX * zoom + panX;
    const toY = v => cy0 - (v - 100) * unitPxY * zoom + panY;
    const cx  = cx0 + panX;
    const cy  = cy0 + panY;

    // Background
    ctx.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--card').trim() || '#fff';
    ctx.fillRect(0, 0, W, H);

    // Clip to chart area
    ctx.save();
    ctx.beginPath();
    ctx.rect(PAD.l, PAD.t, cW, cH);
    ctx.clip();

    // Quadrant fills — areas are always relative to the live crosshair position
    const quadFills = [
      { key: 'leading',   x: cx,    y: PAD.t, w: PAD.l + cW - cx, h: cy - PAD.t      },
      { key: 'weakening', x: cx,    y: cy,    w: PAD.l + cW - cx, h: PAD.t + cH - cy },
      { key: 'lagging',   x: PAD.l, y: cy,    w: cx - PAD.l,      h: PAD.t + cH - cy },
      { key: 'improving', x: PAD.l, y: PAD.t, w: cx - PAD.l,      h: cy - PAD.t      },
    ];
    quadFills.forEach(q => {
      ctx.fillStyle = QUAD_FILL[q.key];
      ctx.fillRect(q.x, q.y, q.w, q.h);
    });

    // Quadrant labels — each pair only shown when its half of the canvas is visible.
    ctx.font = 'bold 9px "IBM Plex Sans", system-ui, sans-serif';
    ctx.letterSpacing = '0.06em';
    ctx.textAlign = 'left';
    const rEdge = PAD.l + cW, bEdge = PAD.t + cH;

    // Right-side labels (Leading / Weakening): only when crosshair is inside the
    // canvas (cx < rEdge). When cx < PAD.l (panned off-screen left) clamp to PAD.l+5.
    if (cx < rEdge) {
      const rightX = Math.min(Math.max(cx + 5, PAD.l + 5), rEdge - 76);
      ctx.fillStyle = QUAD_META.leading.border;
      ctx.fillText('LEADING', rightX, PAD.t + 13);
      ctx.fillStyle = QUAD_META.weakening.border;
      ctx.fillText('WEAKENING', rightX, bEdge - 6);
    }

    // Left-side labels (Improving / Lagging): only when left quadrant has
    // enough room to display the text (cx > PAD.l + 60).
    if (cx > PAD.l + 60) {
      ctx.fillStyle = QUAD_META.improving.border;
      ctx.fillText('IMPROVING', PAD.l + 5, PAD.t + 13);
      ctx.fillStyle = QUAD_META.lagging.border;
      ctx.fillText('LAGGING', PAD.l + 5, bEdge - 6);
    }

    // Crosshairs
    ctx.strokeStyle = 'rgba(0,0,0,.18)';
    ctx.lineWidth   = 0.8;
    ctx.setLineDash([]);
    ctx.beginPath(); ctx.moveTo(cx, PAD.t);  ctx.lineTo(cx, PAD.t + cH); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(PAD.l, cy);  ctx.lineTo(PAD.l + cW, cy); ctx.stroke();

    // Grid
    const vMinX = (-cW / 2 - panX) / (unitPxX * zoom) + 100;
    const vMaxX = ( cW / 2 - panX) / (unitPxX * zoom) + 100;
    const vMinY = 100 + (-cH / 2 + panY) / (unitPxY * zoom);
    const vMaxY = 100 + ( cH / 2 + panY) / (unitPxY * zoom);
    const visRangeX  = vMaxX - vMinX;
    const visRangeY  = vMaxY - vMinY;
    const stepX      = visRangeX <= 3 ? 0.5 : visRangeX <= 8 ? 1 : visRangeX <= 20 ? 2 : 5;
    const stepY      = visRangeY <= 3 ? 0.5 : visRangeY <= 8 ? 1 : visRangeY <= 20 ? 2 : 5;
    const xTickStart = Math.ceil(vMinX / stepX) * stepX;
    const yTickStart = Math.ceil(vMinY / stepY) * stepY;

    ctx.strokeStyle = 'rgba(0,0,0,.05)';
    ctx.lineWidth   = 0.5;
    for (let v = xTickStart; v <= vMaxX + stepX; v += stepX) {
      const px = toX(v);
      if (px < PAD.l - 1 || px > PAD.l + cW + 1) continue;
      ctx.beginPath(); ctx.moveTo(px, PAD.t); ctx.lineTo(px, PAD.t + cH); ctx.stroke();
    }
    for (let v = yTickStart; v <= vMaxY + stepY; v += stepY) {
      const py = toY(v);
      if (py < PAD.t - 1 || py > PAD.t + cH + 1) continue;
      ctx.beginPath(); ctx.moveTo(PAD.l, py); ctx.lineTo(PAD.l + cW, py); ctx.stroke();
    }

    // Sectors with trail animation
    ctx.font = 'bold 9px "IBM Plex Sans", system-ui, sans-serif';
    ctx.letterSpacing = '0';
    const newLabelBounds = [];
    let needsRAF = false;
    const now = performance.now();

    vis.forEach(sec => {
      const fullTrail = sec.trail.slice(-tailLen);
      let trail;

      const startTime = animatingRef.current[sec.id];
      if (startTime !== undefined) {
        const elapsed  = now - startTime;
        const t        = Math.min(elapsed / 1750, 1);
        const progress = 1 - Math.pow(1 - t, 3); // ease-out cubic
        const n        = Math.max(1, Math.ceil(progress * fullTrail.length));
        trail = fullTrail.slice(0, n);
        if (t < 1) needsRAF = true;
        else       delete animatingRef.current[sec.id];
      } else {
        trail = fullTrail;
      }

      const n = trail.length;
      if (n < 1) return;
      const col = secColorFromTrail(trail);

      // Trail lines
      for (let i = 1; i < n; i++) {
        const opacity = 0.12 + (i / (n - 1)) * 0.55;
        const p0 = trail[i - 1], p1 = trail[i];
        ctx.beginPath();
        ctx.moveTo(toX(p0.x), toY(p0.y));
        ctx.lineTo(toX(p1.x), toY(p1.y));
        ctx.strokeStyle = hexToRgba(col, opacity);
        ctx.lineWidth   = 1.5 + (i / (n - 1)) * 1.0;
        ctx.lineJoin    = 'round';
        ctx.lineCap     = 'round';
        ctx.stroke();
      }

      // Trail dots
      for (let i = 0; i < n - 1; i++) {
        const p = trail[i];
        const r = 2 + (i / (n - 1)) * 1.5;
        ctx.beginPath();
        ctx.arc(toX(p.x), toY(p.y), r, 0, Math.PI * 2);
        ctx.fillStyle = hexToRgba(col, 0.15 + (i / (n - 1)) * 0.45);
        ctx.fill();
      }

      // Head dot
      const cur = trail[n - 1];
      const hx  = toX(cur.x), hy = toY(cur.y);
      ctx.beginPath();
      ctx.arc(hx, hy, 5, 0, Math.PI * 2);
      ctx.fillStyle   = hexToRgba(col, 0.9);
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth   = 1.5;
      ctx.stroke();

      // Label
      const label = sec.name.replace(/^Nifty /i, '');
      ctx.font = 'bold 9px "IBM Plex Sans", system-ui, sans-serif';
      const textW = ctx.measureText(label).width;
      const lx = hx + 7, ly = hy + 3;
      ctx.fillStyle = hexToRgba(col, 0.95);
      ctx.textAlign = 'left';
      ctx.fillText(label, lx, ly);

      newLabelBounds.push({ sec, x1: lx - 2,  y1: ly - 10, x2: lx + textW + 2, y2: ly + 4 });
      newLabelBounds.push({ sec, x1: hx - 14, y1: hy - 14, x2: hx + 14,        y2: hy + 14 });
    });

    ctx.restore();

    // Axis tick labels (outside clip, in margins)
    ctx.fillStyle = 'rgba(100,100,120,.7)';
    ctx.font      = '9px "IBM Plex Sans", system-ui, sans-serif';
    ctx.textAlign = 'center';
    for (let v = xTickStart; v <= vMaxX + stepX; v += stepX) {
      const px = toX(v);
      if (px < PAD.l - 5 || px > PAD.l + cW + 5) continue;
      ctx.fillText(Math.round(v * 10) / 10, px, H - 6);
    }
    ctx.textAlign = 'right';
    for (let v = yTickStart; v <= vMaxY + stepY; v += stepY) {
      const py = toY(v);
      if (py < PAD.t - 5 || py > PAD.t + cH + 5) continue;
      ctx.fillText(Math.round(v * 10) / 10, PAD.l - 4, py + 3);
    }

    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(100,100,120,.5)';
    ctx.font      = '9px "IBM Plex Sans", system-ui, sans-serif';
    ctx.fillText('RS-Ratio →', PAD.l + cW / 2, H - 1);
    ctx.save();
    ctx.translate(10, PAD.t + cH / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('RS-Momentum ↑', 0, 0);
    ctx.restore();

    labelBounds.current = newLabelBounds;

    // Keep animating if any sector is mid-animation
    if (needsRAF) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => drawRef.current());
    }
  }, [sectors, tailLen, enabled]);

  const drawRef = useRef(draw);
  useEffect(() => { drawRef.current = draw; });

  useEffect(() => { draw(); }, [draw]);

  useEffect(() => {
    const ob = new ResizeObserver(() => drawRef.current());
    if (canvasRef.current) ob.observe(canvasRef.current);
    return () => ob.disconnect();
  }, []);

  // Reset zoom/pan when sector dataset changes or visible set changes
  useEffect(() => {
    if (sectors.length > 0) {
      zoomRef.current = 1;
      panXRef.current = 0;
      panYRef.current = 0;
    }
  }, [sectors]);

  // Cleanup RAF on unmount
  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  // Non-passive wheel for zoom toward cursor
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (e) => {
      e.preventDefault();
      const rect    = canvas.getBoundingClientRect();
      const mx      = e.clientX - rect.left;
      const my      = e.clientY - rect.top;
      const factor  = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      const newZoom = Math.max(0.3, Math.min(20, zoomRef.current * factor));
      const scale   = newZoom / zoomRef.current;
      panXRef.current = (mx - cx0Ref.current) * (1 - scale) + panXRef.current * scale;
      panYRef.current = (my - cy0Ref.current) * (1 - scale) + panYRef.current * scale;
      zoomRef.current = newZoom;
      drawRef.current();
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, []);

  const handleMouseDown = useCallback((e) => {
    isDragging.current = true;
    setCursor('grabbing');
    dragStart.current = { x: e.clientX, y: e.clientY, panX: panXRef.current, panY: panYRef.current };
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (isDragging.current) {
      panXRef.current = dragStart.current.panX + (e.clientX - dragStart.current.x);
      panYRef.current = dragStart.current.panY + (e.clientY - dragStart.current.y);
      drawRef.current();
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mx   = e.clientX - rect.left;
    const my   = e.clientY - rect.top;
    let found  = null;
    for (const b of labelBounds.current) {
      if (mx >= b.x1 && mx <= b.x2 && my >= b.y1 && my <= b.y2) { found = b.sec; break; }
    }
    setTooltip(found ? { sec: found, x: mx, y: my } : null);
  }, []);

  const handleMouseUp    = useCallback(() => { isDragging.current = false; setCursor('crosshair'); }, []);
  const handleMouseLeave = useCallback(() => { isDragging.current = false; setCursor('crosshair'); setTooltip(null); }, []);

  const resetView = useCallback(() => {
    zoomRef.current = 1; panXRef.current = 0; panYRef.current = 0;
    drawRef.current();
  }, []);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: '100%', display: 'block', cursor }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      />
      <button onClick={resetView} title="Reset zoom &amp; pan" style={{
        position: 'absolute', top: 6, right: 6,
        width: 22, height: 22, borderRadius: 4, padding: 0,
        border: '.5px solid var(--hair-strong)', color: 'var(--ink-3)',
        background: 'var(--surface)', cursor: 'pointer',
        fontSize: 13, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        opacity: 0.6,
      }}>↺</button>

      {tooltip && (() => {
        const s   = tooltip.sec;
        const cur = s.trail[s.trail.length - 1];
        const q   = getQuadrant(cur.x, cur.y);
        return (
          <div style={{
            position: 'absolute', left: tooltip.x + 14, top: tooltip.y - 14,
            background: 'var(--surface)', border: '.5px solid var(--hair-strong)',
            borderRadius: 6, padding: '7px 10px', pointerEvents: 'none',
            boxShadow: '0 2px 12px rgba(0,0,0,.12)', zIndex: 50, minWidth: 148,
          }}>
            <div style={{ fontWeight: 700, fontSize: 11, color: 'var(--ink)', marginBottom: 3 }}>{s.name}</div>
            {s.sector && (
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 1 }}>{s.sector}</div>
            )}
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 2 }}>{s.ticker}</div>
            <div style={{ fontSize: 11, color: 'var(--ink-2)', fontFamily: 'var(--font-data)' }}>
              LTP: ₹{s.ltp?.toLocaleString('en-IN') ?? '—'}
            </div>
            {s.chgPct != null && (
            <div style={{ fontSize: 11, fontFamily: 'var(--font-data)', color: s.chgPct >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)' }}>
              {s.chgPct >= 0 ? '+' : ''}{s.chgPct.toFixed(2)}%
            </div>
            )}
            <div style={{ fontSize: 11, color: QUAD_META[q].text, fontWeight: 700, marginTop: 4 }}>{QUAD_META[q].label}</div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
              RS-Ratio: {cur.x?.toFixed(2) ?? '—'} · RS-Mom: {cur.y?.toFixed(2) ?? '—'}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── Mini RRG Canvas (static, no zoom/pan — used in comparison panel) ──────────
const MINI_PAD = { l: 6, r: 6, t: 20, b: 6 };

function MiniRRGCanvas({ sectors, highlightId, label }) {
  const canvasRef = useRef(null);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !sectors?.length) return;
    const dpr  = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    canvas.width  = rect.width  * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    const W = rect.width, H = rect.height;
    const cW = W - MINI_PAD.l - MINI_PAD.r;
    const cH = H - MINI_PAD.t - MINI_PAD.b;

    // Range from all sectors' last 10 points
    let maxDist = 4;
    sectors.forEach(sec => {
      sec.trail.slice(-10).forEach(pt => {
        maxDist = Math.max(maxDist, Math.abs(pt.x - 100), Math.abs(pt.y - 100));
      });
    });
    const RANGE = maxDist + 1.5;

    const cx0 = MINI_PAD.l + cW / 2;
    const cy0 = MINI_PAD.t + cH / 2;
    const toX = v => cx0 + (v - 100) * (cW / 2) / RANGE;
    const toY = v => cy0 - (v - 100) * (cH / 2) / RANGE;

    // Background
    ctx.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--card').trim() || '#fff';
    ctx.fillRect(0, 0, W, H);

    // Label
    ctx.fillStyle = 'rgba(100,100,120,.8)';
    ctx.font = 'bold 9px "IBM Plex Sans", system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(label, W / 2, 13);

    // Clip
    ctx.save();
    ctx.beginPath();
    ctx.rect(MINI_PAD.l, MINI_PAD.t, cW, cH);
    ctx.clip();

    // Quadrant fills
    [
      { key: 'leading',   x: cx0,          y: MINI_PAD.t,         w: MINI_PAD.l + cW - cx0, h: cy0 - MINI_PAD.t },
      { key: 'weakening', x: cx0,          y: cy0,                 w: MINI_PAD.l + cW - cx0, h: MINI_PAD.t + cH - cy0 },
      { key: 'lagging',   x: MINI_PAD.l,   y: cy0,                 w: cx0 - MINI_PAD.l,       h: MINI_PAD.t + cH - cy0 },
      { key: 'improving', x: MINI_PAD.l,   y: MINI_PAD.t,         w: cx0 - MINI_PAD.l,       h: cy0 - MINI_PAD.t },
    ].forEach(q => {
      ctx.fillStyle = QUAD_FILL[q.key];
      ctx.fillRect(q.x, q.y, q.w, q.h);
    });

    // Crosshairs
    ctx.strokeStyle = 'rgba(0,0,0,.15)';
    ctx.lineWidth   = 0.5;
    ctx.setLineDash([]);
    ctx.beginPath(); ctx.moveTo(cx0, MINI_PAD.t); ctx.lineTo(cx0, MINI_PAD.t + cH); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(MINI_PAD.l, cy0); ctx.lineTo(MINI_PAD.l + cW, cy0); ctx.stroke();

    // All sectors dimmed (head dot only)
    sectors.forEach(sec => {
      if (sec.id === highlightId) return;
      const trail = sec.trail.slice(-10);
      if (!trail.length) return;
      const cur = trail[trail.length - 1];
      const col = secColorFromTrail(trail);
      ctx.beginPath();
      ctx.arc(toX(cur.x), toY(cur.y), 2.5, 0, Math.PI * 2);
      ctx.fillStyle = hexToRgba(col, 0.18);
      ctx.fill();
    });

    // Highlighted sector — trail + head
    const hlSec = sectors.find(s => s.id === highlightId);
    if (hlSec) {
      const trail = hlSec.trail.slice(-10);
      const n     = trail.length;
      if (n > 0) {
        const col = secColorFromTrail(trail);

        for (let i = 1; i < n; i++) {
          const opacity = 0.2 + (i / (n - 1)) * 0.65;
          const p0 = trail[i - 1], p1 = trail[i];
          ctx.beginPath();
          ctx.moveTo(toX(p0.x), toY(p0.y));
          ctx.lineTo(toX(p1.x), toY(p1.y));
          ctx.strokeStyle = hexToRgba(col, opacity);
          ctx.lineWidth   = 1 + (i / (n - 1)) * 1.5;
          ctx.lineJoin    = 'round';
          ctx.lineCap     = 'round';
          ctx.stroke();
        }

        const cur = trail[n - 1];
        ctx.beginPath();
        ctx.arc(toX(cur.x), toY(cur.y), 5, 0, Math.PI * 2);
        ctx.fillStyle   = hexToRgba(col, 0.9);
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth   = 1.5;
        ctx.stroke();

        // RS values overlay
        ctx.fillStyle = hexToRgba(col, 0.85);
        ctx.font      = '9px "IBM Plex Mono", monospace';
        ctx.textAlign = 'right';
        ctx.fillText(`${cur.x.toFixed(1)} / ${cur.y.toFixed(1)}`, MINI_PAD.l + cW - 2, MINI_PAD.t + cH - 3);

        // Quadrant abbr
        const q = getQuadrant(cur.x, cur.y);
        ctx.fillStyle = hexToRgba(QUAD_META[q].text, 0.7);
        ctx.textAlign = 'left';
        ctx.font      = 'bold 9px "IBM Plex Sans",system-ui,sans-serif';
        ctx.fillText(QUAD_META[q].abbr, MINI_PAD.l + 3, MINI_PAD.t + cH - 3);
      }
    }

    ctx.restore();
  }, [sectors, highlightId, label]);

  const drawRef = useRef(draw);
  useEffect(() => { drawRef.current = draw; });
  useEffect(() => { draw(); }, [draw]);
  useEffect(() => {
    const ob = new ResizeObserver(() => drawRef.current());
    if (canvasRef.current) ob.observe(canvasRef.current);
    return () => ob.disconnect();
  }, []);

  return <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />;
}

// ── Quadrant badge cell ────────────────────────────────────────────────────────
function QuadBadge({ pt, dim }) {
  if (!pt) {
    return (
      <span style={{ fontSize: 11, color: 'var(--ink-3)', display: 'inline-block', minWidth: 18, textAlign: 'center' }}>—</span>
    );
  }
  const q = getQuadrant(pt.x, pt.y);
  const m = QUAD_META[q];
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '1px 4px', borderRadius: 2,
      background: dim ? 'transparent' : m.bg,
      color:      dim ? 'var(--hair-strong)' : m.text,
      border:     `.5px solid ${dim ? 'var(--hair)' : m.border}`,
      textTransform: 'uppercase', letterSpacing: '.04em',
      whiteSpace: 'nowrap', display: 'inline-block',
      transition: 'all .15s',
    }}>
      {m.abbr}
    </span>
  );
}

// ── Multi-select dropdown (custom, no OS chrome) ─────────────────────────────
function MultiSelect({ options, selected, onChange, placeholder = 'All', style }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const toggle = val => {
    const next = new Set(selected);
    next.has(val) ? next.delete(val) : next.add(val);
    onChange(next);
  };

  const label = selected.size === 0
    ? placeholder
    : selected.size === options.length
      ? placeholder
      : `${selected.size} sector${selected.size !== 1 ? 's' : ''}`;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block', ...style }}>
      <button
        type="button"
        className="inp"
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer',
          whiteSpace: 'nowrap', minWidth: 100, textAlign: 'left' }}>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 11 }}>{label}</span>
        <span style={{ fontSize: 11, color: 'var(--ink-3)', flexShrink: 0, lineHeight: 1 }}>▾</span>
      </button>
      {open && (
        <div className="sel-drop" style={{ minWidth: 170, maxHeight: 300, overflowY: 'auto', zIndex: 200 }}>
          {/* All / Clear */}
          <div
            className={`sel-opt${selected.size === 0 ? ' sel-on' : ''}`}
            onClick={() => onChange(new Set())}
            style={{ fontStyle: 'italic', borderBottom: '.5px solid var(--hair)' }}>
            All Sectors
          </div>
          {options.map(opt => {
            const on = selected.has(opt);
            const color = SECTOR_CHIP_COLORS[opt] || 'var(--amber-ink)';
            return (
              <div
                key={opt}
                className={`sel-opt${on ? ' sel-on' : ''}`}
                onClick={() => toggle(opt)}
                style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                {/* Tiny checkbox */}
                <span style={{
                  width: 9, height: 9, borderRadius: 2, flexShrink: 0, display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center',
                  border: `.5px solid ${on ? color : 'var(--hair-strong)'}`,
                  background: on ? color : '#fff',
                }}>
                  {on && <svg width="6" height="6" viewBox="0 0 6 6" fill="none">
                    <path d="M1 3L2.5 4.5L5 1.5" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>}
                </span>
                <span style={{ fontSize: 11 }}>{opt}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────────────────
const COMBO_COLS = [
  { key: 'd10', label: 'D·10' },
  { key: 'd20', label: 'D·20' },
  { key: 'w10', label: 'W·10' },
  { key: 'w20', label: 'W·20' },
];

// Quadrant rank: 0 = best (Leading), 3 = weakest (Lagging)
const QUAD_RANK = { leading: 0, improving: 1, weakening: 2, lagging: 3 };
const QUAD_COL  = { leading: '#1a6a1a', improving: '#1a4a8a', weakening: '#8a5a00', lagging: '#881e10' };

function Sidebar({ baseSectors, allData, enabled, onToggle, onOpen, onCompare, compSectorId, sortChain, onSort, onShowAll, onHideAll, quadFilter, onQuadFilter }) {
  function getComboPoint(secId, comboKey) {
    const list = allData?.[comboKey] || [];
    const sec  = list.find(s => s.id === secId);
    if (!sec || !sec.trail?.length) return null;
    return sec.trail[sec.trail.length - 1];
  }

  const sortedSectors = useMemo(() => {
    return [...baseSectors].sort((a, b) => {
      for (const { col, dir } of sortChain) {
        if (col === 'name') {
          const cmp = a.name.localeCompare(b.name);
          if (cmp !== 0) return dir === 'asc' ? cmp : -cmp;
        } else {
          const ptA = getComboPoint(a.id, col);
          const ptB = getComboPoint(b.id, col);
          // Sort purely by quadrant group — fall through to next sort column if tied
          const rA  = ptA ? QUAD_RANK[getQuadrant(ptA.x, ptA.y)] : 4;
          const rB  = ptB ? QUAD_RANK[getQuadrant(ptB.x, ptB.y)] : 4;
          if (rA !== rB) return dir === 'desc' ? rA - rB : rB - rA;
        }
      }
      return 0;
    });
  }, [baseSectors, sortChain, allData]); // eslint-disable-line react-hooks/exhaustive-deps

  const primaryCol = sortChain[0]?.col;
  const primaryDir = sortChain[0]?.dir;

  // Master checkbox state
  const allEnabled  = baseSectors.length > 0 && baseSectors.every(s => enabled.has(s.id));
  const noneEnabled = baseSectors.length === 0 || baseSectors.every(s => !enabled.has(s.id));
  const someEnabled = !allEnabled && !noneEnabled;
  const handleMasterToggle = () => { if (allEnabled) onHideAll(); else onShowAll(); };

  const hdrBase = {
    fontSize: 11, fontWeight: 700, letterSpacing: '.04em',
    textTransform: 'uppercase', cursor: 'pointer', userSelect: 'none',
    whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 2,
  };

  // Compute per-quadrant counts from the sidebar sector list
  const quadCounts = useMemo(() => {
    const counts = { leading: 0, weakening: 0, lagging: 0, improving: 0 };
    baseSectors.forEach(sec => {
      const pt = getComboPoint(sec.id, 'w20') || getComboPoint(sec.id, 'd20');
      if (pt) counts[getQuadrant(pt.x, pt.y)]++;
    });
    return counts;
  }, [baseSectors, allData]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{
      width: 296, flexShrink: 0,
      borderRight: '.5px solid var(--hair)',
      background: 'var(--surface)',
      display: 'flex', flexDirection: 'column',
    }}>

      {/* Quadrant filter pills — TOP of sidebar, above sector list */}
      <div style={{
        padding: '5px 8px',
        borderBottom: '.5px solid var(--hair)',
        flexShrink: 0,
        background: 'var(--surface-2)',
        display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center',
      }}>
        {Object.entries(QUAD_META).map(([k, m]) => {
          const active = quadFilter === k;
          const cnt = quadCounts[k] ?? 0;
          return (
            <button
              key={k}
              onClick={() => onQuadFilter(k)}
              title={active ? `Clear filter (showing ${m.label} only)` : `Show ${m.label} sectors only`}
              style={{
                display: 'flex', alignItems: 'center', gap: 3,
                padding: '2px 5px', borderRadius: 3, cursor: 'pointer',
                border: `.5px solid ${active ? m.border : 'var(--hair-strong)'}`,
                background: active ? m.bg : 'transparent',
                transition: 'all .12s',
              }}>
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '1px 3px', borderRadius: 2,
                background: m.bg, color: m.text, border: `.5px solid ${m.border}`,
                letterSpacing: '.04em',
              }}>{m.abbr}</span>
              <span style={{ fontSize: 11, color: active ? m.text : 'var(--ink-3)', fontWeight: active ? 700 : 400 }}>{cnt}</span>
            </button>
          );
        })}
      </div>

      {/* Header */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '16px 1fr 30px 30px 30px 30px 22px',
        gap: 2, padding: '5px 8px 5px 10px',
        background: 'var(--surface-2)',
        borderBottom: '1px solid var(--hair)',
        flexShrink: 0, alignItems: 'center',
      }}>
        {/* Master tri-state checkbox */}
        <div
          onClick={handleMasterToggle}
          title={allEnabled ? 'Hide all' : 'Show all'}
          style={{
            width: 11, height: 11, borderRadius: 2, cursor: 'pointer',
            border: `.5px solid ${(allEnabled || someEnabled) ? '#4285f4' : 'var(--hair-strong)'}`,
            background: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
          {allEnabled && (
            <svg width="7" height="7" viewBox="0 0 7 7" fill="none">
              <path d="M1 3.5L3 5.5L6 1.5" stroke="#4285f4" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
          {someEnabled && (
            <svg width="7" height="7" viewBox="0 0 7 7" fill="none">
              <path d="M1.5 3.5H5.5" stroke="#4285f4" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          )}
        </div>
        <span
          style={{ ...hdrBase, color: primaryCol === 'name' ? 'var(--amber-ink)' : 'var(--ink-3)' }}
          onClick={() => onSort('name')}>
          Sector {primaryCol === 'name' && (primaryDir === 'asc' ? '▲' : '▼')}
        </span>
        {COMBO_COLS.map(c => (
          <span
            key={c.key}
            style={{ ...hdrBase, color: primaryCol === c.key ? 'var(--amber-ink)' : 'var(--ink-3)', justifyContent: 'center' }}
            onClick={() => onSort(c.key)}>
            {c.label}{primaryCol === c.key && (primaryDir === 'asc' ? '▲' : '▼')}
          </span>
        ))}
        <span />
      </div>

      {/* Rows */}
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {sortedSectors.map(sec => {
          const on          = enabled.has(sec.id);
          const isComparing = compSectorId === sec.id;

          return (
            <div
              key={sec.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '16px 1fr 30px 30px 30px 30px 22px',
                gap: 2, padding: '4px 8px 4px 10px',
                borderBottom: '.5px solid var(--hair)',
                alignItems: 'center',
                opacity: on ? 1 : 0.35,
                background: isComparing ? 'rgba(66,133,244,.06)' : 'transparent',
                transition: 'opacity .15s',
              }}>

              {/* Custom lightweight checkbox — white bg, blue accent */}
              <div
                onClick={e => { e.stopPropagation(); onToggle(sec.id); }}
                title={on ? 'Hide on chart' : 'Show on chart'}
                style={{
                  width: 11, height: 11, borderRadius: 2, cursor: 'pointer',
                  border: `.5px solid ${on ? '#4285f4' : 'var(--hair-strong)'}`,
                  background: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'border-color .12s', flexShrink: 0,
                }}>
                {on && (
                  <svg width="7" height="7" viewBox="0 0 7 7" fill="none">
                    <path d="M1 3.5L3 5.5L6 1.5" stroke="#4285f4" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>

              {/* Sector name → SidePanel */}
              <span
                title={`Open ${sec.ticker} in side panel`}
                onClick={() => onOpen(sec)}
                onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
                style={{
                  fontSize: 11, fontWeight: 600, color: 'var(--amber-ink)',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  cursor: 'pointer',
                }}>
                {sec.name.replace(/^Nifty /i, '')}
              </span>

              {/* 4 badge cells — each toggles the sector */}
              {COMBO_COLS.map(c => (
                <div
                  key={c.key}
                  onClick={() => onToggle(sec.id)}
                  style={{ display: 'flex', justifyContent: 'center', cursor: 'pointer' }}>
                  <QuadBadge pt={getComboPoint(sec.id, c.key)} dim={!on} />
                </div>
              ))}

              {/* ⊞ compare icon */}
              <button
                onClick={() => onCompare(sec.id)}
                title="Compare across D·10 / D·20 / W·10 / W·20"
                style={{
                  width: 18, height: 18, borderRadius: 3, cursor: 'pointer',
                  border: `.5px solid ${isComparing ? 'var(--amber-ink)' : 'var(--hair-strong)'}`,
                  background: isComparing ? 'rgba(66,133,244,.14)' : 'transparent',
                  color: isComparing ? 'var(--amber-ink)' : 'var(--ink-3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, padding: 0, lineHeight: 1,
                  transition: 'all .12s',
                }}>
                ⊞
              </button>
            </div>
          );
        })}

        {sortedSectors.length === 0 && (
          <div style={{ padding: '24px 12px', fontSize: 11, color: 'var(--ink-3)', textAlign: 'center' }}>
            Loading sectors…
          </div>
        )}
      </div>

    </div>
  );
}

// ── Stock RRG Sidebar ─────────────────────────────────────────────────────────
// Left panel for the Stock view. Shows sector filter chips at the top,
// then a flat list of stocks with visibility checkboxes + quadrant badges.

// Colour chip per sector key (compact palette for filter chips)
const SECTOR_CHIP_COLORS = {
  'IT':                   '#4285f4',
  'Banking':              '#1a6a1a',
  'Financial Services':   '#0d8050',
  'Pharmaceuticals':      '#8a2be2',
  'Healthcare':           '#c71585',
  'Automobile':           '#e67e22',
  'Metals':               '#7f8c8d',
  'FMCG':                 '#27ae60',
  'Consumer Durables':    '#16a085',
  'Energy':               '#d35400',
  'Infrastructure':       '#2980b9',
  'Real Estate':          '#8e44ad',
  'Media & Entertainment':'#c0392b',
  'Aviation':             '#2471a3',
  'Cement':               '#a04000',
  'Chemicals':            '#117a65',
  'Conglomerates':        '#626567',
  'Ports & Logistics':    '#1a5276',
  'Retail':               '#7d6608',
  'Technology':           '#1f618d',
  'Telecom':              '#154360',
};

function StockSidebar({
  stockData,
  stockEnabled,
  onToggle,
  onOpen,
  onShowAll,
  onHideAll,
  activeSector,      // string | null — the currently selected sector
  onSelectSector,    // (sector: string) => void — switch active sector
  quadFilter,
  onQuadFilter,
}) {
  const [searchTerm,     setSearchTerm]     = useState('');
  const [expandedSector, setExpandedSector] = useState(null);
  const searchRef = useRef(null);

  // Derive unique sectors (ordered alphabetically)
  const sectors = useMemo(() => {
    const seen = new Set();
    for (const s of stockData) seen.add(s.sector);
    return [...seen].sort();
  }, [stockData]);

  // Filtered flat list for search mode
  const filteredStocks = useMemo(() => {
    if (!searchTerm.trim()) return null;
    const q = searchTerm.trim().toUpperCase();
    return stockData.filter(s => s.id.toUpperCase().includes(q) || (s.name || '').toUpperCase().includes(q));
  }, [searchTerm, stockData]);

  // Stocks grouped by sector, sorted by quadrant rank within sector
  const bySector = useMemo(() => {
    const map = {};
    for (const s of stockData) {
      (map[s.sector] = map[s.sector] || []).push(s);
    }
    for (const sec of Object.keys(map)) {
      map[sec].sort((a, b) => {
        const cA = a.trail[a.trail.length - 1];
        const cB = b.trail[b.trail.length - 1];
        return (cA ? QUAD_RANK[getQuadrant(cA.x, cA.y)] : 4) - (cB ? QUAD_RANK[getQuadrant(cB.x, cB.y)] : 4);
      });
    }
    return map;
  }, [stockData]);

  // Clicking a sector header: if already active → toggle expand/collapse (keep selected)
  // If different sector → expand it + select it (deselects previous)
  const handleHeaderClick = sec => {
    if (sec === activeSector) {
      setExpandedSector(prev => prev === sec ? null : sec);
    } else {
      setExpandedSector(sec);
      onSelectSector(sec);
    }
  };

  const activeSectorStocks = activeSector ? (bySector[activeSector] || []) : [];
  const allEnabled  = activeSectorStocks.length > 0 && activeSectorStocks.every(s => stockEnabled.has(s.id));
  const someEnabled = !allEnabled && activeSectorStocks.some(s => stockEnabled.has(s.id));
  const visibleCount = activeSectorStocks.filter(s => stockEnabled.has(s.id)).length;

  return (
    <div style={{
      width: 270, flexShrink: 0,
      borderRight: '.5px solid var(--hair)',
      background: 'var(--surface)',
      display: 'flex', flexDirection: 'column',
    }}>

      {/* Search bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 4,
        padding: '5px 8px', borderBottom: '.5px solid var(--hair)',
        background: 'var(--surface-2)', flexShrink: 0,
      }}>
        <span style={{ fontSize: 11, color: 'var(--ink-3)', flexShrink: 0 }}>🔍</span>
        <input
          ref={searchRef}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search stock…"
          style={{
            flex: 1, fontSize: 11, padding: '2px 4px',
            background: 'var(--paper)', border: '.5px solid var(--hair-strong)',
            borderRadius: 3, color: 'var(--ink)', outline: 'none',
            fontFamily: 'inherit', minWidth: 0,
          }}
        />
        {searchTerm && (
          <button onClick={() => setSearchTerm('')} style={{
            fontSize: 11, padding: '1px 5px', borderRadius: 3, cursor: 'pointer',
            border: '.5px solid var(--hair-strong)', color: 'var(--ink-3)',
            background: 'transparent', lineHeight: 1, flexShrink: 0,
          }}>✕</button>
        )}
      </div>

      {/* Master row — shows active sector stock count + show/hide all toggle */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '5px 8px 5px 10px', background: 'var(--surface-2)',
        borderBottom: '1px solid var(--hair)', flexShrink: 0,
      }}>
        <div onClick={() => { if (allEnabled) onHideAll(); else onShowAll(); }}
          title={allEnabled ? 'Hide all in sector' : 'Show all in sector'}
          style={{ width: 11, height: 11, borderRadius: 2, cursor: activeSector ? 'pointer' : 'default',
            border: `.5px solid ${(allEnabled || someEnabled) ? '#4285f4' : 'var(--hair-strong)'}`,
            background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {allEnabled && <svg width="7" height="7" viewBox="0 0 7 7" fill="none"><path d="M1 3.5L3 5.5L6 1.5" stroke="#4285f4" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>}
          {someEnabled && <svg width="7" height="7" viewBox="0 0 7 7" fill="none"><path d="M1.5 3.5H5.5" stroke="#4285f4" strokeWidth="1.5" strokeLinecap="round"/></svg>}
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '.04em', color: 'var(--ink-3)' }}>
          {filteredStocks
            ? `${filteredStocks.length} match${filteredStocks.length !== 1 ? 'es' : ''}`
            : activeSector
              ? `${activeSectorStocks.length} stocks · ${visibleCount} visible`
              : 'Select a sector below'}
        </span>
      </div>

      {/* Stock list — flat search results or accordion grouped */}
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {stockData.length === 0 && (
          <div style={{ padding: '20px 12px', fontSize: 11, color: 'var(--ink-3)', textAlign: 'center' }}>
            No F&amp;O stock data available
          </div>
        )}

        {/* ── Search results (flat, no accordion) ── */}
        {filteredStocks && (
          filteredStocks.length === 0
            ? <div style={{ padding: '20px 12px', fontSize: 11, color: 'var(--ink-3)', textAlign: 'center' }}>No matches</div>
            : filteredStocks.map(stock => {
                const on  = stockEnabled.has(stock.id);
                const cur = stock.trail[stock.trail.length - 1];
                const chipColor = SECTOR_CHIP_COLORS[stock.sector] || 'var(--ink-3)';
                return (
                  <div key={stock.id} style={{
                    display: 'grid', gridTemplateColumns: '16px 1fr auto 40px',
                    gap: 4, padding: '3px 8px 3px 10px',
                    borderBottom: '.5px solid var(--hair)', alignItems: 'center',
                    opacity: on ? 1 : 0.35, transition: 'opacity .15s',
                  }}>
                    <div onClick={e => { e.stopPropagation(); onToggle(stock.id); }}
                      style={{ width: 11, height: 11, borderRadius: 2, cursor: 'pointer',
                        border: `.5px solid ${on ? '#4285f4' : 'var(--hair-strong)'}`,
                        background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {on && <svg width="7" height="7" viewBox="0 0 7 7" fill="none"><path d="M1 3.5L3 5.5L6 1.5" stroke="#4285f4" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </div>
                    <span title={`Open ${stock.ticker} in side panel`} onClick={() => onOpen(stock)}
                      onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                      onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
                      style={{ fontSize: 11, fontWeight: 600, color: 'var(--amber-ink)',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}>
                      {stock.id}
                    </span>
                    <span style={{
                      fontSize: 11, padding: '1px 4px', borderRadius: 2,
                      background: `${chipColor}20`, color: chipColor,
                      border: `.5px solid ${chipColor}55`,
                      whiteSpace: 'nowrap', fontWeight: 600,
                    }}>{stock.sector}</span>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      {cur ? <QuadBadge pt={cur} dim={!on} /> : <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>—</span>}
                    </div>
                  </div>
                );
              })
        )}

        {/* ── Accordion (no search active) — one sector open + selected at a time ── */}
        {!filteredStocks && sectors.map(sec => {
          const sectorStocks = bySector[sec] || [];
          const isActive     = sec === activeSector;
          const isExpanded   = sec === expandedSector;
          const color        = SECTOR_CHIP_COLORS[sec] || 'var(--ink-3)';

          return (
            <div key={sec}>
              {/* Sector accordion header — click = select + toggle expand */}
              <div
                onClick={() => handleHeaderClick(sec)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '5px 8px 5px 10px',
                  background: isActive ? `${color}18` : `${color}0a`,
                  borderBottom: `.5px solid ${color}33`,
                  borderLeft: `3px solid ${isActive ? color : 'transparent'}`,
                  cursor: 'pointer', userSelect: 'none',
                  transition: 'background .1s, border-left-color .1s',
                }}>
                <span style={{
                  fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: '.06em', color: isActive ? color : 'var(--ink-3)', flex: 1,
                }}>{sec}</span>
                <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>({sectorStocks.length})</span>
                {isActive && (
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '1px 4px', borderRadius: 2,
                    background: `${color}30`, color, border: `.5px solid ${color}55`,
                    marginLeft: 3, letterSpacing: '.03em',
                  }}>active</span>
                )}
                <span style={{
                  fontSize: 11, color: 'var(--ink-3)',
                  transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                  transition: 'transform .12s', display: 'inline-flex',
                  lineHeight: 1, marginLeft: 4,
                }}>▼</span>
              </div>

              {/* Stock rows — only shown when this sector is expanded */}
              {isExpanded && sectorStocks.map(stock => {
                const on  = stockEnabled.has(stock.id);
                const cur = stock.trail[stock.trail.length - 1];
                return (
                  <div key={stock.id} style={{
                    display: 'grid', gridTemplateColumns: '16px 1fr 56px',
                    gap: 2, padding: '3px 8px 3px 22px',
                    borderBottom: '.5px solid var(--hair)', alignItems: 'center',
                    opacity: on ? 1 : 0.35, transition: 'opacity .15s',
                  }}>
                    <div onClick={e => { e.stopPropagation(); onToggle(stock.id); }}
                      style={{ width: 11, height: 11, borderRadius: 2, cursor: 'pointer',
                        border: `.5px solid ${on ? '#4285f4' : 'var(--hair-strong)'}`,
                        background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {on && <svg width="7" height="7" viewBox="0 0 7 7" fill="none"><path d="M1 3.5L3 5.5L6 1.5" stroke="#4285f4" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </div>
                    <span title={`Open ${stock.ticker} in side panel`} onClick={() => onOpen(stock)}
                      onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                      onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
                      style={{ fontSize: 11, fontWeight: 600, color: 'var(--amber-ink)',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}>
                      {stock.id}
                    </span>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      {cur ? <QuadBadge pt={cur} dim={!on} /> : <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>—</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Footer: note + quadrant filter */}
      <div style={{
        padding: '5px 8px',
        borderTop: '.5px solid var(--hair)',
        flexShrink: 0, background: 'var(--surface-2)',
        display: 'flex', flexDirection: 'column', gap: 5,
      }}>
        <span style={{ fontSize: 11, color: 'var(--ink-3)', fontStyle: 'italic' }}>
          Only 1 sector can be selected at a time
        </span>
        <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: 'var(--ink-3)', marginRight: 2 }}>Filter:</span>
          {Object.entries(QUAD_META).map(([k, m]) => {
            const active = quadFilter === k;
            return (
              <button key={k} onClick={() => onQuadFilter(k)}
                title={active ? 'Clear filter' : `Show ${m.label} stocks only`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 3,
                  padding: '2px 5px', borderRadius: 3, cursor: 'pointer',
                  border: `.5px solid ${active ? m.border : 'var(--hair-strong)'}`,
                  background: active ? m.bg : 'transparent',
                  transition: 'all .12s',
                }}>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '1px 3px', borderRadius: 2,
                  background: m.bg, color: m.text, border: `.5px solid ${m.border}`,
                  letterSpacing: '.04em',
                }}>{m.abbr}</span>
                <span style={{ fontSize: 11, color: active ? m.text : 'var(--ink-3)', fontWeight: active ? 700 : 400 }}>{m.label}</span>
              </button>
            );
          })}
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-3)', fontStyle: 'italic' }}>
            vs sector index
          </span>
        </div>
      </div>
    </div>
  );
}

// ── NIFTY 50 Stock Sidebar ────────────────────────────────────────────────────
// Left panel for Chart 3. Sector chips at the top toggle all stocks in a sector.
// Accordion groups in the list allow per-sector collapse. Per-stock checkboxes.

function NiftyStockSidebar({
  allStocks,
  niftyEnabled,
  onToggle,
  onOpen,
  onShowAll,
  onHideAll,
  sectorFilter,     // Set<string> — empty = show all
  quadFilter,
  onQuadFilter,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy,  setSortBy]  = useState('name');   // 'name' | 'sector' | 'quad'
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = col => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir(col === 'quad' ? 'asc' : 'asc'); }
  };

  // Build display list: sector filter → search → sort
  const displayStocks = useMemo(() => {
    let stocks = sectorFilter.size > 0
      ? allStocks.filter(s => sectorFilter.has(s.sector))
      : allStocks;
    if (searchTerm.trim()) {
      const q = searchTerm.trim().toUpperCase();
      stocks = stocks.filter(s => s.id.toUpperCase().includes(q) || (s.name || '').toUpperCase().includes(q));
    }
    return [...stocks].sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'name') {
        cmp = a.id.localeCompare(b.id);
      } else if (sortBy === 'sector') {
        cmp = (a.sector || '').localeCompare(b.sector || '') || a.id.localeCompare(b.id);
      } else if (sortBy === 'quad') {
        const cA = a.trail[a.trail.length - 1];
        const cB = b.trail[b.trail.length - 1];
        const rA = cA ? QUAD_RANK[getQuadrant(cA.x, cA.y)] : 4;
        const rB = cB ? QUAD_RANK[getQuadrant(cB.x, cB.y)] : 4;
        cmp = rA - rB || a.id.localeCompare(b.id);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [allStocks, sectorFilter, searchTerm, sortBy, sortDir]);

  const allEnabled  = displayStocks.length > 0 && displayStocks.every(s => niftyEnabled.has(s.id));
  const someEnabled = displayStocks.length > 0 && !allEnabled && displayStocks.some(s => niftyEnabled.has(s.id));

  const handleMasterToggle = () => {
    if (allEnabled) {
      displayStocks.forEach(s => { if (niftyEnabled.has(s.id)) onToggle(s.id); });
    } else {
      displayStocks.forEach(s => { if (!niftyEnabled.has(s.id)) onToggle(s.id); });
    }
  };

  const SortHdr = ({ col, children, style: s }) => {
    const active = sortBy === col;
    return (
      <span
        onClick={() => handleSort(col)}
        style={{
          fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em',
          cursor: 'pointer', userSelect: 'none', color: active ? 'var(--amber-ink)' : 'var(--ink-3)',
          display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap', ...s,
        }}>
        {children}{active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
      </span>
    );
  };

  return (
    <div style={{
      width: 280, flexShrink: 0,
      borderRight: '.5px solid var(--hair)',
      background: 'var(--surface)',
      display: 'flex', flexDirection: 'column',
    }}>

      {/* Search bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 4,
        padding: '5px 8px', borderBottom: '.5px solid var(--hair)',
        background: 'var(--surface-2)', flexShrink: 0,
      }}>
        <span style={{ fontSize: 11, color: 'var(--ink-3)', flexShrink: 0 }}>🔍</span>
        <input
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search stock…"
          style={{
            flex: 1, fontSize: 11, padding: '2px 4px',
            background: 'var(--paper)', border: '.5px solid var(--hair-strong)',
            borderRadius: 3, color: 'var(--ink)', outline: 'none',
            fontFamily: 'inherit', minWidth: 0,
          }}
        />
        {searchTerm && (
          <button onClick={() => setSearchTerm('')} style={{
            fontSize: 11, padding: '1px 5px', borderRadius: 3, cursor: 'pointer',
            border: '.5px solid var(--hair-strong)', color: 'var(--ink-3)',
            background: 'transparent', lineHeight: 1, flexShrink: 0,
          }}>✕</button>
        )}
      </div>

      {/* Column header row — master checkbox + sortable columns */}
      <div style={{
        display: 'grid', gridTemplateColumns: '16px 1fr auto 36px',
        gap: 4, padding: '4px 8px 4px 10px',
        background: 'var(--surface-2)', borderBottom: '1px solid var(--hair)',
        flexShrink: 0, alignItems: 'center',
      }}>
        <div
          onClick={handleMasterToggle}
          title={allEnabled ? 'Hide visible' : 'Show visible'}
          style={{
            width: 11, height: 11, borderRadius: 2, cursor: 'pointer',
            border: `.5px solid ${(allEnabled || someEnabled) ? '#4285f4' : 'var(--hair-strong)'}`,
            background: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
          {allEnabled  && <svg width="7" height="7" viewBox="0 0 7 7" fill="none"><path d="M1 3.5L3 5.5L6 1.5" stroke="#4285f4" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>}
          {someEnabled && <svg width="7" height="7" viewBox="0 0 7 7" fill="none"><path d="M1.5 3.5H5.5" stroke="#4285f4" strokeWidth="1.5" strokeLinecap="round"/></svg>}
        </div>
        <SortHdr col="name">Name <span style={{ color: 'var(--ink-3)', fontWeight: 400, fontSize: 11 }}>({displayStocks.length})</span></SortHdr>
        <SortHdr col="sector" style={{ justifyContent: 'flex-end' }}>Sector</SortHdr>
        <SortHdr col="quad" style={{ justifyContent: 'center' }}>Quad</SortHdr>
      </div>

      {/* Flat stock list */}
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {allStocks.length === 0 && (
          <div style={{ padding: '20px 12px', fontSize: 11, color: 'var(--ink-3)', textAlign: 'center' }}>
            No F&amp;O stock data available
          </div>
        )}
        {displayStocks.length === 0 && allStocks.length > 0 && (
          <div style={{ padding: '20px 12px', fontSize: 11, color: 'var(--ink-3)', textAlign: 'center' }}>
            No matches
          </div>
        )}

        {displayStocks.map(stock => {
          const on        = niftyEnabled.has(stock.id);
          const cur       = stock.trail[stock.trail.length - 1];
          const chipColor = SECTOR_CHIP_COLORS[stock.sector] || 'var(--ink-3)';
          return (
            <div key={stock.id} style={{
              display: 'grid', gridTemplateColumns: '16px 1fr auto 36px',
              gap: 4, padding: '3px 8px 3px 10px',
              borderBottom: '.5px solid var(--hair)', alignItems: 'center',
              opacity: on ? 1 : 0.35, transition: 'opacity .15s',
            }}>
              {/* Checkbox */}
              <div
                onClick={e => { e.stopPropagation(); onToggle(stock.id); }}
                style={{ width: 11, height: 11, borderRadius: 2, cursor: 'pointer',
                  border: `.5px solid ${on ? '#4285f4' : 'var(--hair-strong)'}`,
                  background: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {on && <svg width="7" height="7" viewBox="0 0 7 7" fill="none"><path d="M1 3.5L3 5.5L6 1.5" stroke="#4285f4" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              </div>

              {/* Stock name */}
              <span
                title={`Open ${stock.ticker} in side panel`}
                onClick={() => onOpen(stock)}
                onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
                style={{ fontSize: 11, fontWeight: 600, color: 'var(--amber-ink)',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}>
                {stock.id}
              </span>

              {/* Sector pill */}
              <span style={{
                fontSize: 11, padding: '1px 4px', borderRadius: 2,
                background: `${chipColor}20`, color: chipColor,
                border: `.5px solid ${chipColor}55`,
                whiteSpace: 'nowrap', fontWeight: 600,
              }}>{stock.sector}</span>

              {/* Quad badge */}
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                {cur ? <QuadBadge pt={cur} dim={!on} /> : <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>—</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer — quadrant filter */}
      <div style={{
        padding: '4px 8px', borderTop: '.5px solid var(--hair)',
        flexShrink: 0, background: 'var(--surface-2)',
        display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center',
      }}>
        <span style={{ fontSize: 11, color: 'var(--ink-3)', marginRight: 2 }}>Quad:</span>
        {Object.entries(QUAD_META).map(([k, m]) => {
          const active = quadFilter === k;
          return (
            <button key={k} onClick={() => onQuadFilter(k)}
              title={active ? 'Clear quad filter' : `Show ${m.label} only`}
              style={{
                display: 'flex', alignItems: 'center', gap: 3,
                padding: '2px 5px', borderRadius: 3, cursor: 'pointer',
                border: `.5px solid ${active ? m.border : 'var(--hair-strong)'}`,
                background: active ? m.bg : 'transparent', transition: 'all .12s',
              }}>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 3px', borderRadius: 2,
                background: m.bg, color: m.text, border: `.5px solid ${m.border}`,
                letterSpacing: '.04em' }}>{m.abbr}</span>
              <span style={{ fontSize: 11, color: active ? m.text : 'var(--ink-3)', fontWeight: active ? 700 : 400 }}>{m.label}</span>
            </button>
          );
        })}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-3)', fontStyle: 'italic' }}>vs NIFTY 50</span>
      </div>
    </div>
  );
}

// ── Main RRG Tab ──────────────────────────────────────────────────────────────
export default function SectorRotationTab() {
  const { open: openPanel } = useSidePanel();

  // Algorithm / display state (Chart 1) — WMA only, JdK removed
  // Initialised from _PANEL_STATE so values survive tab-switches within the same session.
  const [wmaLength, setWmaLength] = useState(() => _PANEL_STATE.wmaLength);
  const [timeframe, setTimeframe] = useState(() => _PANEL_STATE.timeframe);
  const [tailLen,   setTailLen]   = useState(() => _PANEL_STATE.tailLen);
  const [quadFilter,      setQuadFilter]      = useState(null); // Chart 1 quad filter
  const [stockQuadFilter, setStockQuadFilter] = useState(null); // Chart 2 quad filter
  const [niftyQuadFilter, setNiftyQuadFilter] = useState(null); // Chart 3 quad filter

  // UI state (Chart 1)
  const [enabled,      setEnabled]      = useState(() => new Set());
  const [sortChain,    setSortChain]    = useState([{ col: 'w20', dir: 'desc' }]);
  const [compSectorId, setCompSectorId] = useState(null);

  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 719px)').matches;
  // On mobile the sidebar is collapsed by default so the RRG canvas has full width.
  const [c1SidebarOpen,  setC1SidebarOpen]  = useState(!isMobile);
  const [c2SidebarOpen,  setC2SidebarOpen]  = useState(!isMobile);
  const [c3SidebarOpen,  setC3SidebarOpen]  = useState(!isMobile);

  // Chart 1 open/close (default open; persisted across tab switches)
  const [chart1Open, setChart1Open] = useState(() => _PANEL_STATE.chart1Open);


  // Chart 2 independent controls + open state
  const [stockOpen,       setStockOpen]       = useState(() => _PANEL_STATE.stockOpen);
  const [stockTimeframe,  setStockTimeframe]  = useState(() => _PANEL_STATE.stockTimeframe);
  const [stockWmaLength,  setStockWmaLength]  = useState(() => _PANEL_STATE.stockWmaLength);
  const [stockTailLen,    setStockTailLen]    = useState(() => _PANEL_STATE.stockTailLen);

  // Chart 2 active sector (single-sector selection mode)
  const [activeSector, setActiveSector] = useState(null);

  // Sector filter for Chart 3 (multi-select dropdown)
  const [niftySectorFilter, setNiftySectorFilter] = useState(() => new Set());

  // ── All-4 WMA combos (sidebar badges + comparison panel) ──────────────────
  const [allData,           setAllData]           = useState(null);  // null = not yet loaded
  const [allDataError,      setAllDataError]      = useState(null);
  const [allDataComputedAt, setAllDataComputedAt] = useState(null);
  // Skipped counts are derived from the module-level caches (no separate setState needed).
  const [stockSkipped,          setStockSkipped]          = useState(0);
  const [stockBenchmarkFailed,  setStockBenchmarkFailed]  = useState([]);

  // ── On-demand data (JdK or WMA length 14/26) ──────────────────────────────
  const [odSectors,    setOdSectors]    = useState([]);
  const [odLoading,    setOdLoading]    = useState(false);
  const [odError,      setOdError]      = useState(null);
  const [odComputedAt, setOdComputedAt] = useState(null);
  const [odStale,      setOdStale]      = useState(false);

  // ── Stock section state (Chart 2 — vs sector index) ─────────────────────
  // Sector selection is derived from `enabled` (the sector checkboxes above).
  const [stockData,       setStockData]       = useState([]);
  const [stockEnabled,    setStockEnabled]    = useState(() => new Set());
  const [stockLoading,    setStockLoading]    = useState(false);
  const [stockError,      setStockError]      = useState(null);
  const [stockComputedAt, setStockComputedAt] = useState(null);

  // ── NIFTY 50 section state (Chart 3 — all F&O stocks vs NIFTY 50) ────────
  const [niftyOpen,        setNiftyOpen]        = useState(() => _PANEL_STATE.niftyOpen);
  const [niftyTimeframe,   setNiftyTimeframe]   = useState(() => _PANEL_STATE.niftyTimeframe);
  const [niftyLength,      setNiftyLength]      = useState(() => _PANEL_STATE.niftyLength);
  const [niftyTailLen,     setNiftyTailLen]     = useState(() => _PANEL_STATE.niftyTailLen);
  const [niftyData,        setNiftyData]        = useState([]);
  const [niftyEnabled,     setNiftyEnabled]     = useState(() => new Set());
  const [niftyLoading,     setNiftyLoading]     = useState(false);
  const [niftyError,       setNiftyError]       = useState(null);
  const [niftyComputedAt,  setNiftyComputedAt]  = useState(null);
  const [niftySkipped,     setNiftySkipped]     = useState(0);

  // Persist chart parameters to _PANEL_STATE so they survive tab-switches within the session.
  useEffect(() => {
    _PANEL_STATE.wmaLength      = wmaLength;
    _PANEL_STATE.timeframe      = timeframe;
    _PANEL_STATE.tailLen        = tailLen;
    _PANEL_STATE.stockTimeframe = stockTimeframe;
    _PANEL_STATE.stockWmaLength = stockWmaLength;
    _PANEL_STATE.stockTailLen   = stockTailLen;
    _PANEL_STATE.niftyTimeframe = niftyTimeframe;
    _PANEL_STATE.niftyLength    = niftyLength;
    _PANEL_STATE.niftyTailLen   = niftyTailLen;
  }, [wmaLength, timeframe, tailLen, stockTimeframe, stockWmaLength, stockTailLen,
      niftyTimeframe, niftyLength, niftyTailLen]);

  // Parallel WMA fetch (all 4 combos) — runs once on mount
  useEffect(() => {
    if (allData) return; // already loaded

    setAllDataError(null);

    // Check module-level cache first
    const keys = ['wma_daily_10', 'wma_daily_20', 'wma_weekly_10', 'wma_weekly_20'];
    if (keys.every(k => _cacheValid(_RRG_CACHE[k]))) {
      const ad = {
        d10: _RRG_CACHE['wma_daily_10'].sectors,
        d20: _RRG_CACHE['wma_daily_20'].sectors,
        w10: _RRG_CACHE['wma_weekly_10'].sectors,
        w20: _RRG_CACHE['wma_weekly_20'].sectors,
      };
      setAllData(ad);
      setAllDataComputedAt(_RRG_CACHE['wma_daily_20'].computedAt);
      setEnabled(new Set(ad.d20.map(s => s.id)));
      return;
    }

    // Fetch all 4 combos; treat each independently so a slow weekly compute
    // doesn't block daily data. Weekly combos are retried once after 8 s if
    // the backend is still warming up on first run (~60 s on Railway cold start).
    const fetchCombo = (tf, len) => getRRGData(tf, 52, 'wma', len);

    const loadAll = (retry = false) =>
      Promise.all([
        fetchCombo('daily',  10),
        fetchCombo('daily',  20),
        fetchCombo('weekly', 10),
        fetchCombo('weekly', 20),
      ]).then(([r_d10, r_d20, r_w10, r_w20]) => {
        if (r_d10._error || r_d20._error) {
          setAllDataError(
            'Could not load RRG data. Backend may still be computing — first run can take ~60 s.'
          );
          return;
        }
        const d10 = parseSectors(r_d10);
        const d20 = parseSectors(r_d20);
        const w10 = r_w10._error ? [] : parseSectors(r_w10);
        const w20 = r_w20._error ? [] : parseSectors(r_w20);

        const _now = Date.now();
        if (!r_d10._error) _RRG_CACHE['wma_daily_10']  = { sectors: d10, computedAt: r_d10.computed_at, skipped: r_d10.skipped_count ?? 0, _ts: _now };
        if (!r_d20._error) _RRG_CACHE['wma_daily_20']  = { sectors: d20, computedAt: r_d20.computed_at, skipped: r_d20.skipped_count ?? 0, _ts: _now };
        if (!r_w10._error) _RRG_CACHE['wma_weekly_10'] = { sectors: w10, computedAt: r_w10.computed_at, skipped: r_w10.skipped_count ?? 0, _ts: _now };
        if (!r_w20._error) _RRG_CACHE['wma_weekly_20'] = { sectors: w20, computedAt: r_w20.computed_at, skipped: r_w20.skipped_count ?? 0, _ts: _now };

        const ad = { d10, d20, w10, w20 };
        setAllData(ad);
        setAllDataComputedAt(r_d20.computed_at || null);
        setEnabled(new Set(d20.map(s => s.id)));

        // If weekly combos came back empty (backend still computing), retry once
        const weeklyMissing = (r_w10._error || w10.length === 0) || (r_w20._error || w20.length === 0);
        if (weeklyMissing && !retry) {
          setTimeout(() => {
            Promise.all([fetchCombo('weekly', 10), fetchCombo('weekly', 20)])
              .then(([rw10, rw20]) => {
                const nw10 = rw10._error ? w10 : parseSectors(rw10);
                const nw20 = rw20._error ? w20 : parseSectors(rw20);
                const _nowR = Date.now();
                if (!rw10._error && nw10.length) _RRG_CACHE['wma_weekly_10'] = { sectors: nw10, computedAt: rw10.computed_at, _ts: _nowR };
                if (!rw20._error && nw20.length) _RRG_CACHE['wma_weekly_20'] = { sectors: nw20, computedAt: rw20.computed_at, _ts: _nowR };
                setAllData(prev => prev ? { ...prev, w10: nw10, w20: nw20 } : prev);
              })
              .catch(() => {});
          }, 8000);
        }
      }).catch(() => {
        setAllDataError('Network error loading RRG data.');
      });

    loadAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // On-demand fetch: WMA with non-standard length (not 10 or 20)
  const needsOnDemand = wmaLength !== 10 && wmaLength !== 20;
  const odCacheKey = `wma_${timeframe}_${wmaLength}`;

  useEffect(() => {
    if (!needsOnDemand) return;

    const cached = _RRG_CACHE[odCacheKey];
    if (_cacheValid(cached)) {
      setOdSectors(cached.sectors);
      setOdComputedAt(cached.computedAt);
      setOdStale(false);
      setOdLoading(false);
      setOdError(null);
      return;
    }

    let cancelled = false;
    setOdLoading(true);
    setOdError(null);

    getRRGData(timeframe, 52, 'wma', wmaLength)
      .then(res => {
        if (cancelled) return;
        if (res._error) { setOdError('Could not load RRG data.'); return; }
        const data = parseSectors(res);
        _RRG_CACHE[odCacheKey] = { sectors: data, computedAt: res.computed_at, skipped: res.skipped_count ?? 0, _ts: Date.now() };
        setOdSectors(data);
        setOdComputedAt(res.computed_at || null);
        setOdStale(!!res._stale);
        setOdError(null);
      })
      .catch(() => { if (!cancelled) setOdError('Network error loading RRG.'); })
      .finally(() => { if (!cancelled) setOdLoading(false); });

    return () => { cancelled = true; };
  }, [odCacheKey, needsOnDemand]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Stock data fetch — Chart 2 is now fully independent of Chart 1 ──────────
  // Always fetches all sectors; timeframe and length are Chart 2's own controls.
  useEffect(() => {
    if (!stockOpen) return;

    const cacheKey = `all__wma__${stockTimeframe}__${stockWmaLength}`;
    const _stockCached = _STOCK_RRG_CACHE[cacheKey];
    if (_cacheValid(_stockCached)) {
      const cached = _stockCached;
      setStockData(cached.stocks);
      setActiveSector(prev => {
        const secs = [...new Set(cached.stocks.map(s => s.sector))].sort();
        const next = (prev && secs.includes(prev)) ? prev : (secs[0] || null);
        setStockEnabled(new Set(cached.stocks.filter(s => s.sector === next).map(s => s.id)));
        return next;
      });
      setStockComputedAt(cached.computedAt);
      setStockSkipped(cached.skipped ?? 0);
      setStockBenchmarkFailed(cached.benchmarkFailed ?? []);
      setStockLoading(false);
      setStockError(null);
      return;
    }

    let cancelled = false;
    setStockLoading(true);
    setStockError(null);

    getStockRRGData(ALL_SECTOR_KEYS, stockTimeframe, 'wma', stockWmaLength)
      .then(res => {
        if (cancelled) return;
        if (res._error) { setStockError('Could not load stock RRG data.'); return; }
        const stocks = (res.stocks || []).map(s => ({
          id:      s.id,
          name:    s.label,
          ticker:  s.ticker,
          sector:  s.sector,
          ltp:     s.ltp     ?? 0,
          chg:     s.chg     ?? 0,
          chgPct:  s.chg_pct ?? 0,
          trail:   s.trail   ?? [],
        }));
        const skipped2        = res.skipped_count ?? 0;
        const benchmarkFailed = res.benchmark_failed_sectors ?? [];
        _STOCK_RRG_CACHE[cacheKey] = { stocks, computedAt: res.computed_at, skipped: skipped2, benchmarkFailed, _ts: Date.now() };
        setStockData(stocks);
        setStockSkipped(skipped2);
        setStockBenchmarkFailed(benchmarkFailed);
        setActiveSector(prev => {
          const secs = [...new Set(stocks.map(s => s.sector))].sort();
          const next = (prev && secs.includes(prev)) ? prev : (secs[0] || null);
          setStockEnabled(new Set(stocks.filter(s => s.sector === next).map(s => s.id)));
          return next;
        });
        setStockComputedAt(res.computed_at || null);
        setStockError(null);
      })
      .catch(() => { if (!cancelled) setStockError('Network error loading stock RRG.'); })
      .finally(() => { if (!cancelled) setStockLoading(false); });

    return () => { cancelled = true; };
  }, [stockTimeframe, stockWmaLength, stockOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── NIFTY 50 stock data fetch (Chart 3) ───────────────────────────────────
  useEffect(() => {
    if (!niftyOpen) return;

    const cacheKey = `${niftyTimeframe}__${niftyLength}`;
    const _niftyCached = _NIFTY_STOCK_RRG_CACHE[cacheKey];
    if (_cacheValid(_niftyCached)) {
      const cached = _niftyCached;
      setNiftyData(cached.stocks);
      setNiftyEnabled(new Set(cached.stocks.map(s => s.id)));
      setNiftyComputedAt(cached.computedAt);
      setNiftySkipped(cached.skipped ?? 0);
      setNiftyLoading(false);
      setNiftyError(null);
      return;
    }

    let cancelled = false;
    setNiftyLoading(true);
    setNiftyError(null);

    getStockNiftyRRGData(niftyTimeframe, niftyLength)
      .then(res => {
        if (cancelled) return;
        if (res._error) { setNiftyError('Could not load NIFTY 50 stock RRG data.'); return; }
        const stocks = (res.stocks || []).map(s => ({
          id:     s.id,
          name:   s.label,
          ticker: s.ticker,
          sector: s.sector,
          ltp:    s.ltp     ?? 0,
          chg:    s.chg     ?? 0,
          chgPct: s.chg_pct ?? 0,
          trail:  s.trail   ?? [],
        }));
        const skipped = res.skipped_count ?? 0;
        _NIFTY_STOCK_RRG_CACHE[cacheKey] = { stocks, computedAt: res.computed_at, skipped, _ts: Date.now() };
        setNiftyData(stocks);
        setNiftyEnabled(new Set(stocks.map(s => s.id)));
        setNiftyComputedAt(res.computed_at || null);
        setNiftySkipped(skipped);
        setNiftyError(null);
      })
      .catch(() => { if (!cancelled) setNiftyError('Network error loading NIFTY 50 stock RRG.'); })
      .finally(() => { if (!cancelled) setNiftyLoading(false); });

    return () => { cancelled = true; };
  }, [niftyTimeframe, niftyLength, niftyOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived: main chart sectors ───────────────────────────────────────────
  // Only use allData for lengths 10 and 20 (pre-fetched); other lengths use
  // the on-demand odSectors path even when allData exists.
  const mainSectors = useMemo(() => {
    if (allData && (wmaLength === 10 || wmaLength === 20)) {
      const key = `${timeframe === 'daily' ? 'd' : 'w'}${wmaLength}`;
      return allData[key] || [];
    }
    return odSectors;
  }, [wmaLength, timeframe, allData, odSectors]);

  // Skipped count for Chart 1 — read from whichever cache entry is active.
  const chart1Skipped = useMemo(() => {
    if (wmaLength === 10 || wmaLength === 20) {
      const key = `wma_${timeframe}_${wmaLength}`;
      return _RRG_CACHE[key]?.skipped ?? 0;
    }
    // On-demand fetches store skipped in the od-specific cache key too
    const odKey = `wma_${timeframe}_${wmaLength}`;
    return _RRG_CACHE[odKey]?.skipped ?? 0;
  }, [wmaLength, timeframe, allData, odSectors]); // eslint-disable-line react-hooks/exhaustive-deps

  // Canonical sector list for sidebar rows (d20 = most common selection)
  const baseSectors = allData?.d20 || mainSectors;

  // ── Quadrant filter for Chart 1 ───────────────────────────────────────────
  const filteredForChart1 = useMemo(() => {
    if (!quadFilter) return enabled;
    return new Set([...enabled].filter(id => {
      const sec = mainSectors.find(s => s.id === id);
      if (!sec || !sec.trail.length) return false;
      const cur = sec.trail[sec.trail.length - 1];
      return getQuadrant(cur.x, cur.y) === quadFilter;
    }));
  }, [quadFilter, enabled, mainSectors]);

  // ── Quadrant filter for Chart 2 ───────────────────────────────────────────
  const filteredForChart2 = useMemo(() => {
    if (!stockQuadFilter) return stockEnabled;
    return new Set([...stockEnabled].filter(id => {
      const stock = stockData.find(s => s.id === id);
      if (!stock || !stock.trail.length) return false;
      const cur = stock.trail[stock.trail.length - 1];
      return getQuadrant(cur.x, cur.y) === stockQuadFilter;
    }));
  }, [stockQuadFilter, stockEnabled, stockData]);

  // ── Quadrant filter for Chart 3 ───────────────────────────────────────────
  const filteredForChart3 = useMemo(() => {
    return new Set([...niftyEnabled].filter(id => {
      const stock = niftyData.find(s => s.id === id);
      if (!stock) return false;
      // Sector filter
      if (niftySectorFilter.size > 0 && !niftySectorFilter.has(stock.sector)) return false;
      // Quadrant filter
      if (niftyQuadFilter) {
        if (!stock.trail.length) return false;
        const cur = stock.trail[stock.trail.length - 1];
        if (getQuadrant(cur.x, cur.y) !== niftyQuadFilter) return false;
      }
      return true;
    }));
  }, [niftyQuadFilter, niftySectorFilter, niftyEnabled, niftyData]);

  // ── Quad filter toggle callbacks ──────────────────────────────────────────
  const toggleQuadFilter1 = useCallback(k => setQuadFilter(prev => prev === k ? null : k), []);
  const toggleQuadFilter2 = useCallback(k => setStockQuadFilter(prev => prev === k ? null : k), []);
  const toggleQuadFilter3 = useCallback(k => setNiftyQuadFilter(prev => prev === k ? null : k), [])

  // ── Sort ──────────────────────────────────────────────────────────────────
  const handleSort = useCallback(col => {
    setSortChain(prev => {
      if (prev[0]?.col === col) {
        // Already primary → flip direction
        return [{ col, dir: prev[0].dir === 'desc' ? 'asc' : 'desc' }, ...prev.slice(1)];
      }
      const existing = prev.find(s => s.col === col);
      const newDir   = existing?.dir || 'desc';
      const rest     = prev.filter(s => s.col !== col).slice(0, 2);
      return [{ col, dir: newDir }, ...rest];
    });
  }, []);

  // ── Sector sidebar toggles ────────────────────────────────────────────────
  const toggle = useCallback(id => {
    setEnabled(e => {
      const n = new Set(e);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }, []);

  const showAll = useCallback(() => setEnabled(new Set(baseSectors.map(s => s.id))), [baseSectors]);
  const hideAll = useCallback(() => setEnabled(new Set()), []);

  // ── Stock section toggles (Chart 2) ──────────────────────────────────────
  const toggleStock    = useCallback(id => {
    setStockEnabled(e => { const n = new Set(e); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);
  const showAllStocks  = useCallback(() => {
    if (!activeSector) return;
    setStockEnabled(new Set(stockData.filter(s => s.sector === activeSector).map(s => s.id)));
  }, [stockData, activeSector]);
  const hideAllStocks  = useCallback(() => {
    if (!activeSector) return;
    setStockEnabled(prev => {
      const n = new Set(prev);
      stockData.filter(s => s.sector === activeSector).forEach(s => n.delete(s.id));
      return n;
    });
  }, [stockData, activeSector]);
  const selectStockSector = useCallback(sector => {
    setActiveSector(sector);
    setStockEnabled(new Set(stockData.filter(s => s.sector === sector).map(s => s.id)));
  }, [stockData]);

  // ── Comparison panel ──────────────────────────────────────────────────────
  const handleCompare = useCallback(id => {
    setCompSectorId(prev => prev === id ? null : id);
  }, []);

  // ── SidePanel openers ─────────────────────────────────────────────────────
  const handleOpen = useCallback(sec => {
    const kiteSymbol = RRG_TICKER_TO_KITE[sec.ticker] || sec.ticker;
    openPanel({ name: kiteSymbol });
  }, [openPanel]);

  const handleOpenStock = useCallback(stock => {
    openPanel({ name: `NSE:${stock.ticker}` });
  }, [openPanel]);

  // ── NIFTY 50 stock section toggles ────────────────────────────────────────
  const toggleNifty    = useCallback(id => {
    setNiftyEnabled(e => { const n = new Set(e); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);
  const showAllNifty   = useCallback(() => setNiftyEnabled(new Set(niftyData.map(s => s.id))), [niftyData]);
  const hideAllNifty   = useCallback(() => setNiftyEnabled(new Set()), []);
  const handleOpenNiftyStock = useCallback(stock => {
    openPanel({ name: `NSE:${stock.ticker}` });
  }, [openPanel]);

  // ── Loading / error states ────────────────────────────────────────────────
  const isLoading = (!allData && !allDataError) ||
                    (needsOnDemand && odLoading && odSectors.length === 0);
  const error     = allDataError || odError;

  const computedAt    = allDataComputedAt || odComputedAt;
  const isStale       = odStale;
  const fmtComputedAt = computedAt
    ? new Date(computedAt).toLocaleTimeString('en-IN', {
        hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
      }) + ' IST'
    : null;

  const compSector = compSectorId
    ? (allData?.d20 || mainSectors).find(s => s.id === compSectorId)
    : null;
  const panelOpen = !!(compSectorId && allData && compSector);

  // Stock section header summary
  const fmtStockComputedAt = stockComputedAt
    ? new Date(stockComputedAt).toLocaleTimeString('en-IN', {
        hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
      }) + ' IST'
    : null;

  const fmtNiftyComputedAt = niftyComputedAt
    ? new Date(niftyComputedAt).toLocaleTimeString('en-IN', {
        hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
      }) + ' IST'
    : null;

  if (isLoading) {
    return (
      <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--ink-3)', fontSize: 11 }}>
        Loading RRG… computing all 4 timeframe combinations
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--down-field-ink)', fontSize: 11 }}>
        {error}
      </div>
    );
  }

  // H_NAV(44) + H_SUBTAB(34) = 78 — chart headers freeze below the StickySubTabs row
  const TOP_CHART = 78;

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>

    {/* ══ Section 1: Sector RRG (Chart #1) ════════════════════════════════════ */}
    <div>

      {/* Chart #1 sticky header — collapse toggle row + controls row */}
      <div style={{ position: 'sticky', top: TOP_CHART, zIndex: 20, background: 'var(--paper)' }}>

        {/* Row 1: collapse toggle */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
          background: 'var(--surface-2)', border: '.5px solid var(--hair)',
          borderRadius: chart1Open ? '8px 8px 0 0' : 8,
          userSelect: 'none',
        }}>
          <div onClick={() => { setChart1Open(o => { const n = !o; _PANEL_STATE.chart1Open = n; return n; }); }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, cursor: 'pointer' }}>
            <span style={{ fontSize: 11, color: 'var(--ink-3)',
              transform: chart1Open ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform .15s', display: 'inline-block', lineHeight: 1 }}>▼</span>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ink-2)' }}>
              Sector RRG
            </span>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 6px', borderRadius: 3,
              background: 'rgba(90,60,20,.1)', color: 'var(--ink-2)',
              border: '.5px solid var(--hair-strong)', letterSpacing: '.04em' }}>
              vs NIFTY 50
            </span>
            {!chart1Open && fmtComputedAt && (
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-3)' }}>computed {fmtComputedAt}</span>
            )}
          </div>
          {/* (i) info button */}
          <InfoBtn align="right">Sectors plotted against NIFTY 50. RS-Ratio (x-axis) = how much the sector is outperforming. RS-Momentum (y-axis) = direction and pace of change. Quadrants: Leading (top-right) = strong & strengthening · Weakening (bottom-right) = strong but decelerating · Lagging (bottom-left) = weak & weakening · Improving (top-left) = weak but turning. Canonical default: WMA · Weekly · 20 · Tail 10.</InfoBtn>
        </div>

        {/* Row 2: Timeframe · Length · Tail · Benchmark — only when open */}
        {chart1Open && <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '6px 0 8px',
          borderBottom: '.5px solid var(--hair)',
          flexWrap: 'wrap',
        }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Timeframe:</span>
          {[['daily', 'Daily'], ['weekly', 'Weekly']].map(([v, l]) => (
            <button key={v} className={`pill${timeframe === v ? ' on' : ''}`} onClick={() => setTimeframe(v)}>{l}</button>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Length:</span>
          {[10, 15, 20, 40].map(n => (
            <button key={n} className={`pill${wmaLength === n ? ' on' : ''}`} onClick={() => setWmaLength(n)}>{n}</button>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Tail:</span>
          {[3, 5, 10, 20].map(n => (
            <button key={n} className={`pill${tailLen === n ? ' on' : ''}`} onClick={() => setTailLen(n)}>{n}</button>
          ))}
        </div>

        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-3)' }}>
          Benchmark: NIFTY 50 ·{' '}
          {isStale ? '⚠ stale' : fmtComputedAt ? `computed ${fmtComputedAt}` : 'refreshed daily at 16:00 IST'}
          {chart1Skipped > 0 && (
            <span style={{ color: 'var(--amber-ink)', marginLeft: 6 }}
                  title="Excluded sectors don't yet have enough price history for the WMA RRG calculation">
              · {chart1Skipped} excluded (insufficient history)
            </span>
          )}
        </span>
        </div>}{/* end Row 2 controls */}
      </div>{/* end Chart #1 sticky header */}

      {/* Chart #1 body: comparison panel + sidebar + canvas */}
      {chart1Open && <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 220px)', minHeight: 480 }}>

        {/* Comparison panel (slides in) */}
        <div style={{
          maxHeight: panelOpen ? '220px' : '0px',
          overflow: 'hidden', transition: 'max-height .2s ease-out',
          flexShrink: 0,
          borderBottom: panelOpen ? '.5px solid var(--hair)' : 'none',
          background: 'var(--surface-2)',
        }}>
          {panelOpen && (
            <div style={{ display: 'flex', flexDirection: 'column', height: 220 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                padding: '4px 12px', borderBottom: '.5px solid var(--hair)', flexShrink: 0 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-2)' }}>
                  Comparing: <span style={{ color: 'var(--amber-ink)' }}>{compSector?.name}</span>
                </span>
                <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>— position across all 4 combos</span>
                <button onClick={() => setCompSectorId(null)} style={{
                  marginLeft: 'auto', fontSize: 11, padding: '1px 8px', borderRadius: 2,
                  border: '.5px solid var(--hair-strong)', color: 'var(--ink-3)',
                  background: 'transparent', cursor: 'pointer',
                }}>✕ Close</button>
              </div>
              <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                {[
                  { comboKey: 'd10', sectors: allData?.d10, label: 'Daily · 10' },
                  { comboKey: 'd20', sectors: allData?.d20, label: 'Daily · 20' },
                  { comboKey: 'w10', sectors: allData?.w10, label: 'Weekly · 10' },
                  { comboKey: 'w20', sectors: allData?.w20, label: 'Weekly · 20' },
                ].map(({ comboKey, sectors: comboSectors, label }, i) => (
                  <div key={comboKey} style={{ flex: 1, borderRight: i < 3 ? '.5px solid var(--hair)' : 'none', overflow: 'hidden', padding: 2 }}>
                    <MiniRRGCanvas sectors={comboSectors || []} highlightId={compSectorId} label={label} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Mobile Chart / List toggle */}
        {isMobile && (
          <div style={{ display:'flex', gap:6, padding:'4px 0', flexShrink:0 }}>
            <button className={`pill${!c1SidebarOpen ? ' on' : ''}`}
              onClick={() => setC1SidebarOpen(false)} style={{ fontSize:11 }}>Chart</button>
            <button className={`pill${c1SidebarOpen ? ' on' : ''}`}
              onClick={() => setC1SidebarOpen(true)}  style={{ fontSize:11 }}>☰ List</button>
          </div>
        )}

        {/* Sidebar + canvas */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', border: '.5px solid var(--hair)', borderRadius: 10, marginTop: 4 }}>
          {(!isMobile || c1SidebarOpen) && (
            <Sidebar
              baseSectors={baseSectors}
              allData={allData}
              enabled={enabled}
              onToggle={toggle}
              onOpen={handleOpen}
              onCompare={handleCompare}
              compSectorId={compSectorId}
              sortChain={sortChain}
              onSort={handleSort}
              onShowAll={showAll}
              onHideAll={hideAll}
              quadFilter={quadFilter}
              onQuadFilter={toggleQuadFilter1}
            />
          )}
          {(!isMobile || !c1SidebarOpen) && (
            <div style={{ flex: 1, overflow: 'hidden', padding: 4 }}>
              <RRGCanvas sectors={mainSectors} tailLen={tailLen} enabled={filteredForChart1} />
            </div>
          )}
        </div>
      </div>}{/* end chart1Open body */}
    </div>{/* end Section 1 */}

    {/* ══ Section 2: Stock RRG vs Sector Index (Chart #2, independent) ════════ */}
    <div style={{ marginTop: 16 }}>

      {/* Chart #2 sticky header — collapse toggle + independent controls */}
      <div style={{ position: 'sticky', top: TOP_CHART, zIndex: 15, background: 'var(--paper)' }}>

        {/* Row 1: collapse toggle */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
          background: 'var(--surface-2)', border: '.5px solid var(--hair)',
          borderRadius: stockOpen ? '8px 8px 0 0' : 8,
          userSelect: 'none',
        }}>
          <div onClick={() => { setStockOpen(o => { const n = !o; _PANEL_STATE.stockOpen = n; return n; }); }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, cursor: 'pointer' }}>
            <span style={{ fontSize: 11, color: 'var(--ink-3)',
              transform: stockOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform .15s', display: 'inline-block', lineHeight: 1 }}>▼</span>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ink-2)' }}>
              Stock RRG
            </span>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 6px', borderRadius: 3,
              background: 'rgba(26,106,26,.12)', color: '#1a6a1a',
              border: '.5px solid rgba(26,106,26,.3)', letterSpacing: '.04em' }}>
              vs sector index
            </span>
            {stockData.length > 0 && !stockLoading && (
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-3)' }}>
                {stockData.length} F&amp;O stocks
                {stockSkipped > 0 && (
                  <span style={{ color: 'var(--amber-ink)', marginLeft: 4 }}
                        title="Excluded stocks don't yet have enough price history for the WMA RRG calculation">
                    · {stockSkipped} excluded (insufficient history)
                  </span>
                )}
                {stockBenchmarkFailed.length > 0 && (
                  <span style={{ color: 'var(--amber-ink)', marginLeft: 4 }}
                        title={`Sector benchmark data unavailable — all stocks in these sectors are hidden: ${stockBenchmarkFailed.join(', ')}`}>
                    · {stockBenchmarkFailed.length} sector{stockBenchmarkFailed.length > 1 ? 's' : ''} unavailable (benchmark)
                  </span>
                )}
                {fmtStockComputedAt && ` · computed ${fmtStockComputedAt}`}
              </span>
            )}
            {stockLoading && (
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-3)', fontStyle: 'italic' }}>Computing…</span>
            )}
          </div>
          {/* (i) info button */}
          <InfoBtn align="right">Stocks within a chosen sector plotted against that sector's index (not NIFTY 50). Captures intra-sector relative strength. A stock Leading within Banking may still Lag vs NIFTY — that's expected.</InfoBtn>
        </div>

        {/* Row 2: controls (only when section is open) */}
        {stockOpen && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '5px 0 7px', borderBottom: '.5px solid var(--hair)',
            background: 'var(--paper)', flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Timeframe:</span>
              {[['daily', 'Daily'], ['weekly', 'Weekly']].map(([v, l]) => (
                <button key={v} className={`pill${stockTimeframe === v ? ' on' : ''}`} onClick={() => setStockTimeframe(v)}>{l}</button>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Length:</span>
              {[10, 15, 20, 40].map(n => (
                <button key={n} className={`pill${stockWmaLength === n ? ' on' : ''}`} onClick={() => setStockWmaLength(n)}>{n}</button>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Tail:</span>
              {[3, 5, 10, 20].map(n => (
                <button key={n} className={`pill${stockTailLen === n ? ' on' : ''}`} onClick={() => setStockTailLen(n)}>{n}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Chart #2 body */}
      {stockOpen && (
        <>
          {isMobile && (
            <div style={{ display:'flex', gap:6, padding:'4px 0', flexShrink:0 }}>
              <button className={`pill${!c2SidebarOpen ? ' on' : ''}`}
                onClick={() => setC2SidebarOpen(false)} style={{ fontSize:11 }}>Chart</button>
              <button className={`pill${c2SidebarOpen ? ' on' : ''}`}
                onClick={() => setC2SidebarOpen(true)}  style={{ fontSize:11 }}>☰ List</button>
            </div>
          )}
          <div style={{ display: 'flex', height: 'calc(100vh - 220px)', minHeight: 480, border: '.5px solid var(--hair)', borderTop: 'none', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
            {(!isMobile || c2SidebarOpen) && (
              <StockSidebar
                stockData={stockData}
                stockEnabled={stockEnabled}
                onToggle={toggleStock}
                onOpen={handleOpenStock}
                onShowAll={showAllStocks}
                onHideAll={hideAllStocks}
                activeSector={activeSector}
                onSelectSector={selectStockSector}
                quadFilter={stockQuadFilter}
                onQuadFilter={toggleQuadFilter2}
              />
            )}
            {(!isMobile || !c2SidebarOpen) && (
              <div style={{ flex: 1, overflow: 'hidden', padding: 4, position: 'relative' }}>
                {stockLoading && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, background: 'color-mix(in srgb, var(--surface) 80%, transparent)' }}>
                    <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Computing stock RRG… first run may take a few seconds</span>
                  </div>
                )}
                {stockError && !stockLoading && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 11, color: 'var(--down-field-ink)' }}>{stockError}</span>
                  </div>
                )}
                {!stockLoading && !stockError && stockData.length === 0 && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>No F&amp;O stock data available</span>
                  </div>
                )}
                <RRGCanvas sectors={stockData} tailLen={stockTailLen} enabled={filteredForChart2} />
              </div>
            )}
          </div>
        </>
      )}
    </div>{/* end Section 2 */}

    {/* ══ Section 3: All F&O Stocks vs NIFTY 50 (Chart #3) ════════════════════ */}
    <div style={{ marginTop: 24 }}>

      {/* Chart #3 sticky header — collapse toggle + independent controls */}
      <div style={{ position: 'sticky', top: TOP_CHART, zIndex: 10, background: 'var(--paper)' }}>

        {/* Row 1: collapse toggle */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
          background: 'var(--surface-2)', border: '.5px solid var(--hair)',
          borderRadius: niftyOpen ? '8px 8px 0 0' : 8,
          userSelect: 'none',
        }}>
          <div onClick={() => { setNiftyOpen(o => { const n = !o; _PANEL_STATE.niftyOpen = n; return n; }); }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, cursor: 'pointer' }}>
            <span style={{ fontSize: 11, color: 'var(--ink-3)',
              transform: niftyOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform .15s', display: 'inline-block', lineHeight: 1 }}>▼</span>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ink-2)' }}>
              All F&amp;O Stocks vs NIFTY 50
            </span>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 6px', borderRadius: 3,
              background: 'rgba(26,74,138,.12)', color: '#1a4a8a',
              border: '.5px solid rgba(26,74,138,.3)', letterSpacing: '.04em' }}>
              vs NIFTY 50
            </span>
            {niftyData.length > 0 && !niftyLoading && (
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-3)' }}>
                {niftyData.length} plotted
                {niftySkipped > 0 && (
                  <span style={{ color: 'var(--ink-3)', opacity: 0.7 }}
                        title="Excluded stocks don't yet have enough price history for the RRG calculation (requires ~1–1.5 years of weekly data)">
                    {' · '}{niftySkipped} excluded (no history)
                  </span>
                )}
                {fmtNiftyComputedAt && ` · computed ${fmtNiftyComputedAt}`}
              </span>
            )}
            {niftyLoading && (
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-3)', fontStyle: 'italic' }}>Computing…</span>
            )}
          </div>
          {/* (i) info button */}
          <InfoBtn align="right">All F&amp;O stocks against NIFTY 50. Sector-agnostic basis for comparison. Use the sector filter to narrow without re-anchoring the benchmark.</InfoBtn>
        </div>

        {/* Row 2: controls (only when section is open) */}
        {niftyOpen && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '5px 0 7px', borderBottom: '.5px solid var(--hair)',
            background: 'var(--paper)', flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Timeframe:</span>
              {[['daily', 'Daily'], ['weekly', 'Weekly']].map(([v, l]) => (
                <button key={v} className={`pill${niftyTimeframe === v ? ' on' : ''}`} onClick={() => setNiftyTimeframe(v)}>{l}</button>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Length:</span>
              {[10, 15, 20, 40].map(n => (
                <button key={n} className={`pill${niftyLength === n ? ' on' : ''}`} onClick={() => setNiftyLength(n)}>{n}</button>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Tail:</span>
              {[3, 5, 10, 20].map(n => (
                <button key={n} className={`pill${niftyTailLen === n ? ' on' : ''}`} onClick={() => setNiftyTailLen(n)}>{n}</button>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 8 }}>
              <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Sector:</span>
              <MultiSelect
                options={ALL_SECTOR_KEYS}
                selected={niftySectorFilter}
                onChange={setNiftySectorFilter}
                placeholder="All Sectors"
                style={{ minWidth: 110 }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Chart #3 body */}
      {niftyOpen && (
        <>
          {isMobile && (
            <div style={{ display:'flex', gap:6, padding:'4px 0', flexShrink:0 }}>
              <button className={`pill${!c3SidebarOpen ? ' on' : ''}`}
                onClick={() => setC3SidebarOpen(false)} style={{ fontSize:11 }}>Chart</button>
              <button className={`pill${c3SidebarOpen ? ' on' : ''}`}
                onClick={() => setC3SidebarOpen(true)}  style={{ fontSize:11 }}>☰ List</button>
            </div>
          )}
          <div style={{ display: 'flex', height: 'calc(100vh - 220px)', minHeight: 480, border: '.5px solid var(--hair)', borderTop: 'none', borderRadius: '0 0 8px 8px', overflow: 'hidden', marginBottom: 24 }}>
            {(!isMobile || c3SidebarOpen) && (
              <NiftyStockSidebar
                allStocks={niftyData}
                niftyEnabled={niftyEnabled}
                onToggle={toggleNifty}
                onOpen={handleOpenNiftyStock}
                onShowAll={showAllNifty}
                onHideAll={hideAllNifty}
                sectorFilter={niftySectorFilter}
                quadFilter={niftyQuadFilter}
                onQuadFilter={toggleQuadFilter3}
              />
            )}
            {(!isMobile || !c3SidebarOpen) && (
              <div style={{ flex: 1, overflow: 'hidden', padding: 4, position: 'relative' }}>
                {niftyLoading && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, background: 'color-mix(in srgb, var(--surface) 80%, transparent)' }}>
                    <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Computing NIFTY 50 RRG… first run may take 30–60 s</span>
                  </div>
                )}
                {niftyError && !niftyLoading && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 11, color: 'var(--down-field-ink)' }}>{niftyError}</span>
                  </div>
                )}
                {!niftyLoading && !niftyError && niftyData.length === 0 && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>No F&amp;O stock data available — expand this section and wait for data to load</span>
                  </div>
                )}
                <RRGCanvas sectors={niftyData} tailLen={niftyTailLen} enabled={filteredForChart3} />
              </div>
            )}
          </div>
        </>
      )}
    </div>{/* end Section 3 */}

    </div>
  );
}
